import { USER_AUTH_RESPONSE } from "./user";

type UnknownRecord = Record<string, any>;

/**
 * Normalizes auth/authenticate payloads from the current backend contract into
 * a stable FE shape for the NGXS user store.
 *
 * Why this adapter exists:
 * - Login and authenticate flows do not guarantee the same payload structure.
 * - The request interceptor can wrap login errors into an HttpResponse-like
 *   success path with `status` merged into the body.
 * - The store should depend on one normalized model instead of backend field
 *   variants such as `userDetail`, `userProfile`, `error`, and `errorCode`.
 */
export interface NormalizedLoginResponse {
  isLoggedInSuccess: boolean;
  isUserSessionActive: boolean;
  isUpdatePassword: boolean;
  isPasswordExpiring: boolean;
  isPasswordExpired: boolean;
  passwordExpiringInDays: number | null;
  lastLoginDate: string | null;
  redirectUrl: string | null;
  statusCode: number | string | null;
  errorCode: string | null;
  error: string | null;
  message: string | null;
  userDetail: Partial<USER_AUTH_RESPONSE> | null;
}

export function normalizeAuthenticatedUser(
  payload: unknown,
): Partial<USER_AUTH_RESPONSE> {
  const response = extractResponseBody(payload);
  const userDetail = asRecord(response["userDetail"]);
  const userProfile = asRecord(response["userProfile"]);
  let source;
  if (Object.keys(userDetail).length > 0) {
    source = userDetail;
  } else if (Object.keys(userProfile).length > 0) {
    source = userProfile;
  } else {
    source = response;
  }
  const userId = firstString(
    source["userId"],
    response["userId"],
    response["username"],
  );
  const displayName = buildDisplayName(source, response, userId);
  const menuAccess = ensureStringArray(source["menuAccess"]);
  const menuList = ensureArray(source["menuList"]);

  return {
    ...source,
    userId,
    name: firstString(source["name"], displayName),
    preferName: firstString(
      source["preferName"],
      response["firstName"],
      displayName,
    ),
    mainUserId: firstString(source["mainUserId"], userId),
    status: firstString(source["status"], "ACTIVE"),
    userType: firstString(source["userType"], deriveUserType(source, response)),
    redirectUrl: firstString(source["redirectUrl"], response["redirectUrl"]),
    error: firstString(source["error"], response["error"]),
    adminType: firstString(source["adminType"], deriveAdminType(source)),
    lastLoginDate: firstString(
      source["lastLoginDate"],
      response["lastLoginDate"],
    ),
    menuAccess,
    menuList,
    admin: toBoolean(source["admin"]) || toBoolean(source["isAdmin"]),
    agent: toBoolean(source["agent"]) || toBoolean(source["isAgent"]),
    bdmadmin: toBoolean(source["bdmadmin"]) || toBoolean(source["isBDMAdmin"]),
    branchAdmin:
      toBoolean(source["branchAdmin"]) || toBoolean(source["isBranchAdmin"]),
    callCenter: toBoolean(source["callCenter"]),
    callCentreOrBancaAgent: toBoolean(source["callCentreOrBancaAgent"]),
    createBy: firstString(source["createBy"]),
    hqAdmin: toBoolean(source["hqAdmin"]) || toBoolean(source["isHqAdmin"]),
    issueOnBehalf: toBoolean(source["issueOnBehalf"]),
    issueOnEndorsement: toBoolean(source["issueOnEndorsement"]),
    issueOnQuotation: toBoolean(source["issueOnQuotation"]),
    newNric: firstString(source["newNric"]),
    staffInd: toBoolean(source["staffInd"]),
    viewOwnTrans: toBoolean(source["viewOwnTrans"]),
  };
}

export function normalizeLoginResponse(
  payload: unknown,
): NormalizedLoginResponse {
  const root = asRecord(payload);
  const statusCode = toStatusCode(root["status"]);
  const response = extractResponseBody(payload);
  const userDetail = normalizeAuthenticatedUser(response);
  const errorCode = firstString(response["errorCode"]);
  const error = firstString(response["error"]);
  const userId = firstString(userDetail.userId, response["userId"]);
  const isSuccess =
    !errorCode &&
    !error &&
    (toBoolean(response["isLoggedInSuccess"]) ||
      Object.keys(asRecord(response["userProfile"])).length > 0 ||
      Object.keys(asRecord(response["userDetail"])).length > 0 ||
      !!userId);

  return {
    isLoggedInSuccess: isSuccess,
    isUserSessionActive:
      toBoolean(response["isUserSessionActive"]) || statusCode === 409,
    isUpdatePassword: toBoolean(response["isUpdatePassword"]),
    isPasswordExpiring: toBoolean(response["isPasswordExpiring"]),
    isPasswordExpired: toBoolean(response["isPasswordExpired"]),
    passwordExpiringInDays: toNumber(response["passwordExpiringInDays"]),
    lastLoginDate: firstString(
      response["lastLoginDate"],
      userDetail.lastLoginDate,
    ),
    redirectUrl: firstString(response["redirectUrl"], userDetail.redirectUrl),
    statusCode,
    errorCode: errorCode ?? null,
    error: error ?? null,
    message: firstString(response["message"], response["errorDesc"]),
    userDetail: isSuccess ? userDetail : null,
  };
}

function extractResponseBody(payload: unknown): UnknownRecord {
  const root = asRecord(payload);
  const errorBody = root["error"];
  if (errorBody && typeof errorBody === "object" && !Array.isArray(errorBody)) {
    return asRecord(errorBody);
  }
  return root;
}

function asRecord(value: unknown): UnknownRecord {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return value as UnknownRecord;
}

function ensureStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function ensureArray<T = any>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

function toBoolean(value: unknown): boolean {
  return value === true;
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toStatusCode(value: unknown): number | string | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }
  return null;
}

function buildDisplayName(
  source: UnknownRecord,
  response: UnknownRecord,
  fallbackUserId: string | null,
): string {
  const explicitName = firstString(source["name"]);
  if (explicitName) {
    return explicitName;
  }

  const fullName = [response["firstName"], response["lastName"]]
    .filter(
      (value): value is string =>
        typeof value === "string" && value.trim().length > 0,
    )
    .join(" ")
    .trim();
  if (fullName) {
    return fullName;
  }

  return firstString(response["username"], fallbackUserId) ?? "";
}

function deriveUserType(
  source: UnknownRecord,
  response: UnknownRecord,
): string | null {
  if (toBoolean(source["admin"]) || toBoolean(source["hqAdmin"])) {
    return "AD";
  }
  if (toBoolean(source["agent"])) {
    return "AG";
  }

  const roles = ensureStringArray(response["roles"]);
  if (roles.includes("ROLE_AGENT")) {
    return "AG";
  }
  if (roles.includes("ROLE_ADMIN")) {
    return "AD";
  }

  return null;
}

function deriveAdminType(source: UnknownRecord): string | null {
  if (toBoolean(source["bdmadmin"])) {
    return "BDM";
  }
  if (toBoolean(source["branchAdmin"])) {
    return "BR";
  }
  if (toBoolean(source["hqAdmin"])) {
    return "HQ";
  }
  return null;
}
